import { useCallback, useEffect, useState } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import { ErroDeArea, EstadoVazio, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { inteiro, lerGrafico, lerInsightsDoAdmin, reais } from '../../paineis';
import type { AtalhoDePeriodo, DimensaoDoGrafico, Grafico, MetricaDoGrafico } from '../../paineis';
import { SubnavDoPainel } from './SubnavDoPainel';
import { GaleriaDeGraficos } from './GaleriaDeGraficos';
import {
  BarrasComBase,
  CartaoDoPainel,
  LegendaDasBarras,
  ROTULO_DO_ATALHO,
  baixarCsv,
  periodoDaUrl,
  reaisCompacto,
  textoDoPeriodo,
  trocarConsulta,
} from './pecasDoPainel';
import type { Celula } from './pecasDoPainel';
import './graficos.css';

/**
 * TELA 12 A — GRÁFICOS. Um explorador: métrica × dimensão × período × comparação, e três cortes
 * que o dado atual sustenta (dia da semana, hora × dia da semana, composição por tipo).
 *
 * O estado mora na URL (?metrica=&por=&p=), para que os links dos Insights abram o corte certo.
 * Corte que a fonte não sustenta (cupons ou ticket por balconista/tipo) é recusado pelo servidor,
 * e a tela mostra o motivo dele — não inventa uma divisão.
 */

const METRICAS: readonly { m: MetricaDoGrafico; rotulo: string }[] = [
  { m: 'faturamento', rotulo: 'Faturamento' },
  { m: 'itens', rotulo: 'Itens' },
  { m: 'cupons', rotulo: 'Cupons' },
  { m: 'ticket', rotulo: 'Ticket médio' },
];
const DIMENSOES: readonly { d: DimensaoDoGrafico; rotulo: string; titulo: string }[] = [
  { d: 'hora', rotulo: 'Hora', titulo: 'por hora' },
  { d: 'dia', rotulo: 'Dia', titulo: 'por dia' },
  { d: 'dia_semana', rotulo: 'Dia da semana', titulo: 'por dia da semana' },
  { d: 'balconista', rotulo: 'Balconista', titulo: 'por balconista' },
  { d: 'tipo', rotulo: 'Tipo', titulo: 'por tipo' },
];
const ATALHOS: readonly AtalhoDePeriodo[] = ['hoje', 'ontem', '7d', '30d', 'mes'];
const DIAS_DA_SEMANA = [
  { d: 1, r: 'seg' },
  { d: 2, r: 'ter' },
  { d: 3, r: 'qua' },
  { d: 4, r: 'qui' },
  { d: 5, r: 'sex' },
  { d: 6, r: 'sáb' },
  { d: 0, r: 'dom' },
];

const formatoDa = (m: MetricaDoGrafico) =>
  m === 'faturamento' ? reaisCompacto : m === 'ticket' ? (v: number) => reais(v) : inteiro;

type Estado =
  | { fase: 'carregando' }
  | { fase: 'erro' }
  | { fase: 'sem-acesso' }
  | { fase: 'pronto'; g: Grafico };

export function Graficos({ eu, parametros }: { eu: Eu | null; parametros: URLSearchParams }) {
  const metricaUrl = parametros.get('metrica') as MetricaDoGrafico | null;
  const porUrl = parametros.get('por') as DimensaoDoGrafico | null;
  const metrica = METRICAS.some((x) => x.m === metricaUrl)
    ? (metricaUrl as MetricaDoGrafico)
    : 'faturamento';
  const por = DIMENSOES.some((x) => x.d === porUrl) ? (porUrl as DimensaoDoGrafico) : 'hora';
  const comparar = parametros.get('comparar') !== 'nao';
  const { periodo, atalho } = periodoDaUrl(parametros, '7d');
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [nInsights, setNInsights] = useState<number | undefined>(undefined);

  const carregar = useCallback(() => {
    let vivo = true;
    setEstado({ fase: 'carregando' });
    void lerGrafico({ metrica, por, periodo, comparar }).then((r) => {
      if (!vivo) return;
      if (r.ok) setEstado({ fase: 'pronto', g: r.dados });
      else setEstado({ fase: r.falha.tipo === 'sem-acesso' ? 'sem-acesso' : 'erro' });
    });
    return () => {
      vivo = false;
    };
  }, [metrica, por, periodo.de, periodo.ate, comparar]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    let vivo = true;
    // o mesmo número da página Insights: cartões que pedem ação (atenção + oportunidade)
    void lerInsightsDoAdmin().then(
      (r) =>
        vivo &&
        r.ok &&
        setNInsights(
          r.dados.grupos
            .flatMap((g) => g.cartoes)
            .filter((c) => c.tom === 'alerta' || c.tom === 'oportunidade').length,
        ),
    );
    return () => {
      vivo = false;
    };
  }, []);

  if ((eu && !podeFazer(eu, 'painel.ver_completo')) || estado.fase === 'sem-acesso')
    return <SemAcesso area="Gráficos" />;

  const g = estado.fase === 'pronto' ? estado.g : null;
  const nomeDaMetrica = METRICAS.find((x) => x.m === metrica)?.rotulo ?? '';
  const dim = DIMENSOES.find((x) => x.d === por);
  const rotuloDoPeriodo =
    atalho === 'personalizado' ? textoDoPeriodo(periodo) : ROTULO_DO_ATALHO[atalho];
  const mudar = (m: Record<string, string | null>) => trocarConsulta(parametros, m);

  return (
    <div className="grf">
      <SubnavDoPainel atual="graficos" insights={nInsights} />
      <div className="grf-corpo">
        <div className="grf-topo">
          <div className="grf-titulo">
            <h1>Gráficos</h1>
            <p>
              {textoDoPeriodo(periodo)}
              {g?.fonte
                ? ` · ${g.fonte === 'relatorio_hos' ? 'vendas do HOS' : 'lançamentos do InovaPay'}`
                : ''}
              {g?.comparar ? ` · comparado com ${textoDoPeriodo(g.comparar)}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="grf-botao"
            disabled={!g}
            onClick={() => g && exportarCsv(g)}
          >
            <Icone nome="down" tamanho={15} traco={1.6} />
            Exportar dados
          </button>
        </div>

        <GaleriaDeGraficos periodo={periodo} />

        <div className="gx-secao-cabeca grf-montar">
          <h2>Montar o seu gráfico</h2>
          <p>Escolha a medida, o corte e o período — e exporte os dados.</p>
        </div>
        <div className="grf-controles">
          <label className="grf-campo">
            <span className="grf-rotulo">MÉTRICA</span>
            <span className="grf-select grf-select-metrica">
              <select value={metrica} onChange={(e) => mudar({ metrica: e.target.value })}>
                {METRICAS.map((x) => (
                  <option key={x.m} value={x.m}>
                    {x.rotulo}
                  </option>
                ))}
              </select>
              <Icone nome="chev-d" tamanho={14} traco={1.6} />
            </span>
          </label>
          <div className="grf-campo">
            <span className="grf-rotulo">VISUALIZAR POR</span>
            <div className="grf-segmento" role="tablist" aria-label="Visualizar por">
              {DIMENSOES.map((x) => (
                <button
                  key={x.d}
                  type="button"
                  role="tab"
                  aria-selected={por === x.d}
                  className={por === x.d ? 'grf-sel' : ''}
                  onClick={() => mudar({ por: x.d })}
                >
                  {x.rotulo}
                </button>
              ))}
            </div>
          </div>
          <span className="grf-select">
            <Icone nome="cal" tamanho={14} traco={1.5} />
            <select
              aria-label="Período"
              value={atalho}
              onChange={(e) => mudar({ p: e.target.value, de: null, ate: null })}
            >
              {ATALHOS.map((a) => (
                <option key={a} value={a}>
                  {ROTULO_DO_ATALHO[a]}
                </option>
              ))}
              {atalho === 'personalizado' && (
                <option value="personalizado" disabled>
                  {rotuloDoPeriodo}
                </option>
              )}
            </select>
            <Icone nome="chev-d" tamanho={14} traco={1.6} />
          </span>
          <span className="grf-select">
            <span className="grf-select-pre">Comparar</span>
            <select
              aria-label="Comparar"
              value={comparar ? 'sim' : 'nao'}
              onChange={(e) => mudar({ comparar: e.target.value === 'nao' ? 'nao' : null })}
            >
              <option value="sim">Período anterior</option>
              <option value="nao">Sem comparação</option>
            </select>
            <Icone nome="chev-d" tamanho={14} traco={1.6} />
          </span>
          <span className="grf-flex" />
          <a className="grf-limpar" href="#/graficos">
            Limpar
          </a>
        </div>

        {estado.fase === 'erro' ? (
          <ErroDeArea oQue="este gráfico" aoTentar={carregar} />
        ) : (
          <>
            <CartaoDoPainel
              className="grf-principal"
              titulo={`${nomeDaMetrica} ${dim?.titulo ?? ''} · ${rotuloDoPeriodo}`}
              sub={subtituloDoPrincipal(metrica, por)}
              direita={
                g && g.pontos.length > 0 ? (
                  <LegendaDasBarras atual="Atual" base={g.comparar ? 'Anterior' : null} />
                ) : undefined
              }
            >
              <div className="grf-principal-corpo">
                {!g ? (
                  <div className="grf-carregando" aria-busy="true" />
                ) : g.pontos.length === 0 ? (
                  <EstadoVazio
                    icone={g.porque?.includes('use itens') ? 'lock' : 'clock'}
                    titulo={
                      g.porque?.includes('use itens')
                        ? 'Este corte não existe para esta métrica'
                        : 'Ainda não há dados suficientes para este período'
                    }
                    texto={
                      g.porque
                        ? `${g.porque.charAt(0).toUpperCase()}${g.porque.slice(1)}.`
                        : 'Nenhuma venda no período.'
                    }
                  />
                ) : por === 'balconista' || por === 'tipo' ? (
                  <BarrasDeitadas g={g} />
                ) : (
                  <BarrasComBase
                    descricao={`${nomeDaMetrica} ${dim?.titulo ?? ''}`}
                    pontos={g.pontos.map((p) => ({
                      rotulo: p.rotulo,
                      atual: p.atual,
                      base: p.anterior,
                      dica: p.rotulo.toUpperCase(),
                    }))}
                    formato={formatoDa(metrica)}
                    rotuloAtual="Atual"
                    rotuloBase={g.comparar ? 'Anterior' : null}
                    cadaRotulo={
                      por === 'hora'
                        ? 2
                        : por === 'dia'
                          ? Math.max(1, Math.ceil(g.pontos.length / 10))
                          : 1
                    }
                  />
                )}
              </div>
            </CartaoDoPainel>

            <div className="grf-cortes">
              <CartaoDoPainel
                titulo="Por dia da semana"
                sub={`${metrica === 'ticket' ? 'ticket médio' : 'faturamento médio por dia'} · últimas 4 semanas`}
              >
                <div className="grf-corte-corpo">{g && <PorDiaDaSemana g={g} />}</div>
              </CartaoDoPainel>
              <CartaoDoPainel
                titulo="Hora × dia da semana"
                sub="intensidade de faturamento · 4 semanas"
              >
                <div className="grf-corte-corpo">{g && <Calor g={g} />}</div>
              </CartaoDoPainel>
              <CartaoDoPainel titulo="Composição por tipo" sub="itens lançados no período">
                <div className="grf-corte-corpo">{g && <Composicao g={g} />}</div>
              </CartaoDoPainel>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function subtituloDoPrincipal(m: MetricaDoGrafico, por: DimensaoDoGrafico): string {
  if (por === 'hora')
    return m === 'ticket'
      ? 'Ticket de cada faixa horária no período'
      : 'Soma por faixa horária no período';
  if (por === 'dia') return 'Cada dia do período · a comparação alinha o 1º dia com o 1º dia';
  if (por === 'dia_semana') return 'Média por dia de cada dia da semana no período';
  return 'Lançamentos do InovaPay no período';
}

function BarrasDeitadas({ g }: { g: Grafico }) {
  const fmt = formatoDa(g.metrica);
  const max = Math.max(1, ...g.pontos.map((p) => Math.max(p.atual, p.anterior ?? 0)));
  return (
    <ul className="grf-deitadas">
      {g.pontos.map((p) => (
        <li key={p.chave}>
          <span className="grf-deitada-nome" title={p.rotulo}>
            {p.rotulo}
          </span>
          <span className="grf-deitada-trilho">
            <i style={{ width: `${String((p.atual / max) * 100)}%` }} />
            {p.anterior !== null && (
              <b
                className="grf-deitada-marca"
                style={{ left: `${String((p.anterior / max) * 100)}%` }}
                title={`Anterior: ${fmt(p.anterior)}`}
              />
            )}
          </span>
          <span className="grf-deitada-valor">
            {fmt(p.atual)}
            {p.anterior !== null && <small>antes {fmt(p.anterior)}</small>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PorDiaDaSemana({ g }: { g: Grafico }) {
  if (g.semana.every((s) => s.media === null))
    return (
      <EstadoVazio
        icone="cal"
        titulo="Sem venda nas últimas 4 semanas"
        texto="Nenhuma venda do HOS nem lançamento cobre o período."
      />
    );
  const fmt = g.metrica === 'ticket' ? (v: number) => reais(v) : reaisCompacto;
  return (
    <BarrasComBase
      descricao="Média por dia da semana nas últimas 4 semanas"
      pontos={g.semana.map((s) => ({
        rotulo: s.rotulo,
        atual: s.media ?? 0,
        base: null,
        dica: s.media === null ? `${s.rotulo.toUpperCase()} · sem venda` : s.rotulo.toUpperCase(),
      }))}
      formato={fmt}
      rotuloAtual="Média"
      rotuloBase={null}
    />
  );
}

function Calor({ g }: { g: Grafico }) {
  if (g.calor.length === 0)
    return (
      <EstadoVazio
        icone="clock"
        titulo="Sem venda do HOS nas últimas 4 semanas"
        texto="O mapa de hora × dia só existe com o relatório importado."
      />
    );
  const horas = g.calor.map((c) => c.hora);
  const h0 = Math.min(...horas);
  const h1 = Math.max(...horas);
  const colunas: number[] = [];
  for (let h = h0; h <= h1; h += 1) colunas.push(h);
  const dias = DIAS_DA_SEMANA.filter((d) => g.calor.some((c) => c.dia === d.d));
  const max = Math.max(...g.calor.map((c) => c.valor), 1);
  const valor = (d: number, h: number) =>
    g.calor.find((c) => c.dia === d && c.hora === h)?.valor ?? 0;
  const nivel = (v: number) => (v <= 0 ? 0 : Math.min(6, Math.ceil((v / max) * 6)));
  const cada = colunas.length > 8 ? 2 : 1;
  return (
    <div
      className="grf-calor"
      role="img"
      aria-label="Faturamento por hora e dia da semana, últimas 4 semanas"
    >
      {dias.map((d) => (
        <div key={d.d} className="grf-calor-linha">
          <span className="grf-calor-dia">{d.r}</span>
          {colunas.map((h) => {
            const v = valor(d.d, h);
            return (
              <i
                key={h}
                className={`grf-n${String(nivel(v))}`}
                title={`${d.r} ${String(h).padStart(2, '0')}h · ${reaisCompacto(v)}`}
              />
            );
          })}
        </div>
      ))}
      <div className="grf-calor-linha grf-calor-eixo">
        <span className="grf-calor-dia" />
        {colunas.map((h, k) => (
          <span key={h}>{k % cada === 0 ? String(h).padStart(2, '0') : ''}</span>
        ))}
      </div>
      <div className="grf-calor-legenda">
        <span>menor</span>
        <i />
        <span>maior</span>
      </div>
    </div>
  );
}

function Composicao({ g }: { g: Grafico }) {
  if (g.composicao.length === 0)
    return <EstadoVazio icone="box" titulo="Nenhum lançamento no período" />;
  return (
    <ul className="grf-composicao">
      {g.composicao.map((c, k) => (
        <li key={c.tipo}>
          <div>
            <span>{c.tipo}</span>
            <b>{String(c.pct).replace('.', ',')}%</b>
          </div>
          <span className="grf-comp-trilho">
            <i
              className={`grf-comp-${String(Math.min(k, 3))}`}
              style={{ width: `${String(c.pct)}%` }}
            />
          </span>
          <small>{inteiro(c.itens)} itens</small>
        </li>
      ))}
    </ul>
  );
}

function exportarCsv(g: Grafico) {
  const fonte =
    g.fonte === 'relatorio_hos'
      ? 'Vendas do HOS'
      : g.fonte === 'inovapay'
        ? 'lançamentos do InovaPay'
        : '';
  const linhas: Celula[][] = [
    ['Gráfico', `${g.metrica} por ${g.por}`],
    ['Período', `${g.periodo.de} a ${g.periodo.ate}`],
    ['Comparado com', g.comparar ? `${g.comparar.de} a ${g.comparar.ate}` : 'sem comparação'],
    ['Fonte', fonte || (g.porque ?? '')],
    [],
    ['Chave', 'Rótulo', 'Atual', 'Anterior', 'Fonte'],
    ...g.pontos.map((p): Celula[] => [p.chave, p.rotulo, p.atual, p.anterior, fonte]),
    [],
    ['Dia da semana', 'Média (últimas 4 semanas)', 'Fonte'],
    ...g.semana.map((s): Celula[] => [s.rotulo, s.media, 'Vendas do HOS ou lançamentos']),
    [],
    ['Dia da semana (0=dom)', 'Hora', 'Faturamento (R$, 4 semanas)', 'Fonte'],
    ...g.calor.map((c): Celula[] => [c.dia, c.hora, c.valor, 'Vendas do HOS']),
    [],
    ['Tipo', 'Itens', '%', 'Fonte'],
    ...g.composicao.map((c): Celula[] => [c.tipo, c.itens, c.pct, 'lançamentos do InovaPay']),
  ];
  baixarCsv(`graficos-${g.metrica}-${g.por}-${g.periodo.de}-a-${g.periodo.ate}.csv`, linhas);
}
